/**
 * Vesper Demo Service (Shared HTTP client)
 *
 * Thin wrapper around POST /demo/{start,heartbeat,end}. Used by the
 * DemoPicker page (A9) and any heartbeat poller.
 *
 * The /start endpoint is unauthenticated — it issues the JWT. /heartbeat
 * and /end require the demo JWT (issued by /start, stored in ark_token).
 */

import apiClient from './api.client';
import { getApiBase } from '../constants/api';
import type { VesperRole, UserPublicProfile } from '../types/user.types';

/**
 * The 4 fixed Demo Mode archetypes (per ADR-023). These are NOT the same
 * thing as VesperRole on a real user — they're the entry-point archetypes
 * for a demo session, but the resulting clone *does* carry one of these
 * values in its `role` field.
 */
export type DemoArchetype = Extract<VesperRole, 'founder' | 'investor' | 'operator' | 'mentor'>;

export interface StartDemoPayload {
  archetype: DemoArchetype;
  /** Optional caller GPS — if outside the top-10 fallback cities by >50 miles, server snaps to nearest. */
  lat?: number;
  lng?: number;
}

/** When the demo service overrode the viewer's location (low local event
 *  density, GPS too far from any fallback, etc.), this note explains what
 *  happened. The frontend reads it to surface a one-time toast on landing
 *  so the override is transparent. Absent (undefined) means viewer GPS was
 *  used as-is. */
export interface DemoSpoofNote {
  spoofed:             boolean;
  originalCity?:       string;
  spoofedTo?:          string;
  originalEventCount?: number;
}

export interface StartDemoResult {
  token:     string;
  /** Cloned archetype user's public profile — same shape as login/register response. */
  user:      UserPublicProfile;
  expiresAt: string;
  userId:    string;
  sessionId: string;
  spoofNote?: DemoSpoofNote;
}

export async function startDemoSession(payload: StartDemoPayload): Promise<StartDemoResult> {
  // Native fetch instead of axios — same workaround as listPublicArchetypes.
  // The apiClient instance was timing out on the demo picker tab in some
  // browser contexts even though native fetch to the same URL succeeded
  // (Content-Type override + interceptor cleanup didn't fix it; suspected
  // stuck HTTP/2 stream bound to axios's keep-alive pool but not fetch's).
  // Demo start is unauthenticated and has no useful interceptor work, so
  // dropping axios is a clean bypass. Manual 45s timeout via AbortController
  // matches the original override (heavy operation: clones 4 archetypes,
  // places 1500 cohort users, attaches ~800 RSVPs to seeded events).
  const g = globalThis as unknown as {
    fetch: (url: string, init: { method: string; headers: Record<string, string>; body: string; signal: unknown }) => Promise<{ ok: boolean; status: number; json(): Promise<unknown> }>;
    AbortController: new () => { abort: () => void; signal: unknown };
    setTimeout: (fn: () => void, ms: number) => unknown;
    clearTimeout: (id: unknown) => void;
    console: { log: (...args: unknown[]) => void; error: (...args: unknown[]) => void };
  };
  const ctrl = new g.AbortController();
  const timeoutId = g.setTimeout(() => ctrl.abort(), 45_000);
  const url = `${getApiBase()}/demo/start`;
  g.console.log('[startDemoSession] POST', url, payload);
  const t0 = Date.now();
  try {
    const res = await g.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: ctrl.signal,
    });
    g.console.log('[startDemoSession] response status', res.status, 'in', Date.now() - t0, 'ms');
    if (!res.ok) {
      throw new Error(`startDemoSession HTTP ${res.status}`);
    }
    const body = await res.json() as { data: StartDemoResult };
    g.console.log('[startDemoSession] parsed body, returning');
    return body.data;
  } catch (err) {
    g.console.error('[startDemoSession] ERROR after', Date.now() - t0, 'ms:', err);
    throw err;
  } finally {
    g.clearTimeout(timeoutId);
  }
}

export interface HeartbeatResult {
  expiresAt: string;
}

export async function heartbeatDemoSession(sessionId: string): Promise<HeartbeatResult> {
  const res = await apiClient.post<{ data: HeartbeatResult }>('/demo/heartbeat', { sessionId });
  return res.data.data;
}

export async function endDemoSession(sessionId: string): Promise<void> {
  await apiClient.post('/demo/end', { sessionId });
}

/**
 * Fire a single analytics event for the active demo session. The backend
 * never errors on bad payloads (returns 200 even when dropping), but if
 * the network fails we swallow silently — analytics must never break a
 * demo flow.
 */
export interface DemoEventInput {
  sessionId: string;
  eventType: 'surface_visit' | 'action';
  eventName: string;
  surface?: string;
  metadata?: Record<string, unknown>;
}

export async function recordDemoEvent(input: DemoEventInput): Promise<void> {
  try {
    await apiClient.post('/demo/event', input);
  } catch {
    // Intentional: analytics is best-effort, must never throw to the caller.
  }
}

/** Public archetype display info — drives the /demo picker page. Real avatar
 *  URLs come from canonical archetype rows so admin avatar refreshes propagate
 *  without a frontend redeploy. */
export interface PublicArchetypeInfo {
  archetype:   DemoArchetype;
  displayName: string;
  avatarUrl:   string | null;
  gender:      'man' | 'woman' | 'nonbinary' | null;
  sectorTags:  string[];
}

export async function listPublicArchetypes(): Promise<PublicArchetypeInfo[]> {
  // Native fetch instead of axios — diagnostic-driven. axios via apiClient
  // was timing out at 15s in some browser contexts even though native fetch
  // to the same URL succeeded instantly. Public, unauthenticated read; the
  // axios layer's interceptors (token injection, 401/403 redirects) add no
  // value here, so dropping them removes a class of bug at the same time.
  // globalThis.fetch is universal across web + React Native runtime.
  const fetchFn = (globalThis as unknown as { fetch: (url: string) => Promise<{ ok: boolean; status: number; json(): Promise<unknown> }> }).fetch;
  const res = await fetchFn(`${getApiBase()}/demo/archetypes`);
  if (!res.ok) {
    throw new Error(`listPublicArchetypes HTTP ${res.status}`);
  }
  const body = await res.json() as { data: PublicArchetypeInfo[] };
  return body.data;
}

/**
 * Returns 12 random demo cohort avatar URLs for the onboarding sendoff /
 * plan-reveal galaxy spiral animation. Cohort avatars live on the
 * Unsplash CDN (seeded by seedDemoCohort.ts), so they render
 * cross-browser. Public, unauthenticated.
 *
 * Returns an empty array on failure — caller should provide its own
 * fallback handling.
 */
export async function getSpiralAvatars(): Promise<string[]> {
  try {
    const fetchFn = (globalThis as unknown as { fetch: (url: string) => Promise<{ ok: boolean; status: number; json(): Promise<unknown> }> }).fetch;
    const res = await fetchFn(`${getApiBase()}/demo/spiral-avatars`);
    if (!res.ok) return [];
    const body = await res.json() as { data: string[] };
    return Array.isArray(body.data) ? body.data : [];
  } catch {
    return [];
  }
}
